import { useQuery, useMutation, useQueryClient } from 'react-query';
import { predictionManagerAxiosApi } from 'src/Config/axiosApi';
import { ManualConfirmedPair } from '../types/entry.types';
import { adaptPair, PairManagerPair } from './useEntry';

/**
 * Opener pair groups — operator-defined buckets of manual-confirmed pairs
 * used to scope the opener page (and bulk actions) to one group at a time.
 * Lives on the pair-manager service alongside `/pairs`, so all hooks here
 * re-use the existing `predictionManagerAxiosApi`.
 */
export interface OpenerGroupCategoryRule {
  category: string;
  subcategory?: string;
}

export interface OpenerGroup {
  _id: string;
  name: string;
  color?: string;
  sortOrder?: number;
  makerQuietReevalSeconds?: number | null;
  counterStuckMarketHedgeSeconds?: number | null;
  /**
   * Group-level placement-gate-disable default (tri-state). `true`/`false`
   * override; `null` (or absent) = inherit the global default. Per-platform:
   * Polymarket maker leg.
   */
  disablePlacementGatePolymarket?: boolean | null;
  /** Group-level placement-gate-disable default for the Kalshi maker leg. */
  disablePlacementGateKalshi?: boolean | null;
  counterMakerWalkStepSeconds?: number | null;
  counterMakerWalkStepCents?: number | null;
  lockstepMaxAheadBatches?: number | null;
  categoryRule?: OpenerGroupCategoryRule | null;
  dynamic: boolean;
  /**
   * When true the group last started against the separate low-balance TEST
   * account. Set at START time (the Start split-button), honored only while
   * the group is off (backend-enforced); read here only for the badge.
   */
  testAccount?: boolean;
  pairCount: number;
  /**
   * Stored "default config" snapshot for the group — captured via
   * `useSaveGroupDefaultConfig` and re-applied to every member rule (config
   * only, never enable/maker toggles) via `useResetGroupDefaultConfig`. Null
   * until the operator saves one.
   */
  defaultConfig?: Record<string, unknown> | null;
  /** ISO timestamp of the last default-config save, or null if none. */
  defaultConfigSavedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateOpenerGroupDto {
  name: string;
  color?: string;
  sortOrder?: number;
  makerQuietReevalSeconds?: number | null;
  counterStuckMarketHedgeSeconds?: number | null;
  disablePlacementGatePolymarket?: boolean | null;
  disablePlacementGateKalshi?: boolean | null;
  counterMakerWalkStepSeconds?: number | null;
  counterMakerWalkStepCents?: number | null;
  lockstepMaxAheadBatches?: number | null;
  dynamic?: boolean;
  categoryRule?: OpenerGroupCategoryRule;
}

export type UpdateOpenerGroupDto = Partial<CreateOpenerGroupDto>;

export interface PairCategory {
  category: string;
  subcategory: string | null;
  pairCount: number;
}

/** Real GET /opener-groups wire shape (see GroupsService.list()). */
export interface OpenerGroupsResponse {
  groups: OpenerGroup[];
  ungroupedCount: number;
}

export function useOpenerGroups() {
  return useQuery<OpenerGroup[]>(
    ['opener-groups'],
    async () => {
      const { data } = await predictionManagerAxiosApi.get<
        OpenerGroupsResponse | OpenerGroup[]
      >('/opener-groups');
      // Canonical wire shape is `{ groups, ungroupedCount }`; the bare-array
      // branch is defensive only (a malformed/legacy payload must not crash
      // every consumer that spreads the result).
      return Array.isArray(data) ? data : (data?.groups ?? []);
    },
    { refetchInterval: 30_000, keepPreviousData: true }
  );
}

/**
 * Group CRUD. A 409 (duplicate name / duplicate categoryRule) propagates to
 * the caller via `mutateAsync` so the create/edit dialog can show an inline
 * error; the global MutationCache.onError backstop still sees the failure.
 * Mutating a group can change pair assignments (dynamic category rules,
 * delete-unassigns), so `manual-confirmed-pairs` is invalidated too.
 */
export function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation(
    async (dto: CreateOpenerGroupDto) => {
      const { data } = await predictionManagerAxiosApi.post<OpenerGroup>(
        '/opener-groups',
        dto
      );
      return data;
    },
    {
      mutationKey: 'entry:create-group',
      onSuccess: () => {
        qc.invalidateQueries(['opener-groups']);
        qc.invalidateQueries('manual-confirmed-pairs');
      }
    }
  );
}

export function useUpdateGroup() {
  const qc = useQueryClient();
  return useMutation(
    async ({ id, dto }: { id: string; dto: UpdateOpenerGroupDto }) => {
      const { data } = await predictionManagerAxiosApi.patch<OpenerGroup>(
        `/opener-groups/${id}`,
        dto
      );
      return data;
    },
    {
      mutationKey: 'entry:update-group',
      onSuccess: () => {
        qc.invalidateQueries(['opener-groups']);
        qc.invalidateQueries('manual-confirmed-pairs');
      }
    }
  );
}

export function useDeleteGroup() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      const { data } = await predictionManagerAxiosApi.delete<{
        deleted: boolean;
        unassigned: number;
      }>(`/opener-groups/${id}`);
      return data;
    },
    {
      mutationKey: 'entry:delete-group',
      onSuccess: () => {
        qc.invalidateQueries(['opener-groups']);
        qc.invalidateQueries('manual-confirmed-pairs');
      }
    }
  );
}

/**
 * Distinct categories/subcategories across manual-confirmed pairs, with
 * per-bucket pair counts. Dialog-only data — gate with `enabled` so it
 * isn't fetched until the group dialog opens.
 */
export function usePairCategories(enabled = true) {
  return useQuery<PairCategory[]>(
    ['pair-categories'],
    async () => {
      const { data } = await predictionManagerAxiosApi.get<PairCategory[]>(
        '/pairs/categories'
      );
      return data;
    },
    { enabled, keepPreviousData: true }
  );
}

export function useAssignPairGroup() {
  const qc = useQueryClient();
  return useMutation(
    async ({
      pairId,
      groupId
    }: {
      pairId: string;
      groupId: string | null;
    }) => {
      const { data } = await predictionManagerAxiosApi.patch(
        `/pairs/${pairId}/group`,
        { groupId }
      );
      return data;
    },
    {
      mutationKey: 'entry:assign-pair-group',
      onSuccess: () => {
        qc.invalidateQueries('manual-confirmed-pairs');
        qc.invalidateQueries(['opener-groups']);
      }
    }
  );
}

/** Backend caps `pairIds` at 100 per request. */
export const BULK_ASSIGN_CHUNK_SIZE = 100;

/**
 * Assign (or unassign with `groupId: null`) many pairs in chunks of 100 —
 * callers pass ONE id array; the chunking happens here, sequentially, and
 * the per-chunk `matched`/`modified` counters are summed into one result.
 */
export async function bulkAssignPairGroup(
  pairIds: string[],
  groupId: string | null
): Promise<{ matched: number; modified: number }> {
  let matched = 0;
  let modified = 0;
  for (let i = 0; i < pairIds.length; i += BULK_ASSIGN_CHUNK_SIZE) {
    const { data } = await predictionManagerAxiosApi.patch<{
      matched: number;
      modified: number;
    }>('/pairs/group/bulk', {
      pairIds: pairIds.slice(i, i + BULK_ASSIGN_CHUNK_SIZE),
      groupId
    });
    matched += data.matched;
    modified += data.modified;
  }
  return { matched, modified };
}

export function useBulkAssignPairGroup() {
  const qc = useQueryClient();
  return useMutation(
    ({ pairIds, groupId }: { pairIds: string[]; groupId: string | null }) =>
      bulkAssignPairGroup(pairIds, groupId),
    {
      mutationKey: 'entry:bulk-assign-pair-group',
      onSuccess: () => {
        qc.invalidateQueries('manual-confirmed-pairs');
        qc.invalidateQueries(['opener-groups']);
      }
    }
  );
}

/**
 * Hidden manual-confirmed pairs (hidden-from-opener), for the "show hidden"
 * management view. Same pair shape + `adaptPair` flattening as
 * `useManualConfirmedPairs`; gated by `enabled` so it only fetches while
 * that view is open.
 */
export function useHiddenManualConfirmedPairs(enabled: boolean) {
  return useQuery<ManualConfirmedPair[]>(
    ['manual-confirmed-pairs', 'hidden'],
    async () => {
      const { data } = await predictionManagerAxiosApi.get<PairManagerPair[]>(
        '/pairs/manual-confirmed',
        { params: { status: 'active', hidden: 'only' } }
      );
      return Array.isArray(data) ? data.map(adaptPair) : [];
    },
    { enabled, refetchInterval: 30_000, keepPreviousData: true }
  );
}
